/**
 * React Query hooks for MCP Setup Assistant operations.
 *
 * Tenant-scoped: setup info, initiate OAuth flow, process callback.
 *
 * Sprint: 63, Stream B, Task: B4
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  SetupInfo,
  SetupInitiateResponse,
  SetupCallbackResponse,
  SetupInitiateRequest,
  SetupCallbackRequest,
} from '../setup-types';

// =============================================================================
// Query Keys
// =============================================================================

export const setupKeys = {
  all: ['mcp-setup'] as const,
  info: (tenantId: string, slug: string) =>
    [...setupKeys.all, 'info', tenantId, slug] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Get setup assistant info for a template.
 *
 * Returns mode, display config, and pre-config fields so the
 * frontend can render the correct UI (OAuth button / wizard / manual).
 */
export function useSetupInfo(slug: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: setupKeys.info(tenantId!, slug),
    queryFn: async () => {
      const { data } = await apiClient.get<SetupInfo>(`/api/v1/mcp/setup/${slug}/info`);
      return data;
    },
    enabled: !!tenantId && !!slug,
  });
}

/**
 * Initiate a setup assistant flow (OAuth redirect, wizard start, etc.).
 *
 * For OAuth modes, returns an authorization_url + state.
 * The frontend opens a popup with authorization_url and waits for the callback.
 */
export function useSetupInitiate() {
  return useMutation({
    mutationFn: async ({
      slug,
      data: mutationData,
    }: {
      slug: string;
      data?: SetupInitiateRequest;
    }) => {
      const { data } = await apiClient.post<SetupInitiateResponse>(
        `/api/v1/mcp/setup/${slug}/initiate`,
        mutationData ?? {},
      );
      return data;
    },
  });
}

/**
 * Process a setup assistant callback (OAuth code exchange → create MCP server).
 *
 * Called after the OAuth popup closes with code + state.
 * The backend validates state, exchanges the code, and creates the MCPServer.
 */
export function useSetupCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flowType,
      data: mutationData,
    }: {
      flowType: string;
      data: SetupCallbackRequest;
    }) => {
      const { data } = await apiClient.post<SetupCallbackResponse>(
        `/api/v1/mcp/setup/${flowType}/callback`,
        mutationData,
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate server lists so the new server appears immediately
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
    },
  });
}
